import React      from 'react';
import { Helmet } from 'react-helmet';
import { Link }   from 'react-router-dom';
import BlogFooter from 'components/BlogFooter';
import styles     from './styles.module.scss';

const CANONICAL        = 'https://trytrivia.co/donut-alternative/';
const SEO_TITLE        = 'Donut Alternative: Coffee + Games | Trivia';
const META_DESCRIPTION = 
  'Donut alternative for HR: 1:1 VirtualCoffee pairing plus 5-minute games in Slack and Microsoft Teams. Add to Slack or Add to Teams.';

const COMPARISON_ROWS = [
  {
    need  : '1:1 coffee / intros',
    donut : 'Slack and Teams',
    trivia: 'VirtualCoffee pairing. Slack and Teams.'
  },
  {
    need  : 'Ice breakers / prompts',
    donut : 'Conversation starters around intros',
    trivia: 'Ice breakers in the pairing DM'
  },
  {
    need  : '5-minute channel quizzes / Pictionary',
    donut : "Donut's core is intros, onboarding, and recognition",
    trivia: 'Same Trivia install, Slack and Teams'
  },
  {
    need  : 'Hangman',
    donut : 'n/a',
    trivia: 'Slack only'
  },
  {
    need  : 'Calendar hold',
    donut : 'Donut markets calendar and video tools',
    trivia: 'Slack catch-up hold. Teams pairing has no calendar hold.'
  }
];

const FAQ_ITEMS = [
  {
    question: 'Is Trivia a Donut alternative?',
    answer  : 
      'Yes, if you want 1:1 coffee pairing plus five-minute channel games in one Slack or Microsoft Teams install. Stay on Donut if intros, onboarding, and recognition are the whole program.'
  },
  {
    question: 'Does VirtualCoffee work in Microsoft Teams?',
    answer  : 'Yes. Pairing is live in Slack and in Microsoft Teams.'
  },
  {
    question: 'Can Teams put the coffee on the calendar?',
    answer  : 
      'No. Slack pairs can book a calendar hold. Teams pairing still sends the 1:1 DM and ice breakers. People pick a time themselves.'
  },
  {
    question: 'Is Hangman on Teams?',
    answer  : 'No. Hangman is Slack-only.'
  },
  {
    question: 'Do we need both Donut and Trivia?',
    answer  : 'Not if the job is pairing plus quizzes. One Trivia install covers both.'
  }
];

const CtaButtons = ({ className }) => (
  <div className = {className}>
  <a   className = {styles.primaryCta} href = {process.env.REACT_APP_SLACK_INSTALL}>
      Add to Slack
    </a>
    <a className = {styles.secondaryCta} href = {process.env.REACT_APP_TEAMS_INSTALL}>
      Add to Teams
    </a>
  </div>
);

const DonutAlternative = () => (
  <main className = {styles.page}>
    <Helmet>
      <title>{SEO_TITLE}</title>
      <meta name     = 'description' content    = {META_DESCRIPTION} />
      <meta name     = 'robots' content         = 'index, follow' />
      <link rel      = 'canonical' href         = {CANONICAL} />
      <meta property = 'og:title' content       = {SEO_TITLE} />
      <meta property = 'og:description' content = {META_DESCRIPTION} />
      <meta property = 'og:url' content         = {CANONICAL} />
      <meta property = 'og:type' content        = 'website' />
    </Helmet>

    <header className = {styles.navigation}>
    <div    className = {styles.navigationInner}>
    <a      className = {styles.logo} href = 'https://trytrivia.co/'>
    <span   className = {styles.logoMark}>?</span>
          <span>Trivia</span>
        </a>
        <nav  className = {styles.navLinks} aria-label = 'Main navigation'>
        <a    href      = 'https://trytrivia.co/slack/'>Slack</a>
        <a    href      = 'https://trytrivia.co/microsoft-teams/'>Teams</a>
        <Link to        = '/blog'>Blog</Link>
        <a    className = {styles.loginLink} href      = {process.env.REACT_APP_SLACK_INSTALL}>
            Add to Slack
          </a>
        </nav>
      </div>
    </header>

    <section className = {styles.hero}>
    <div     className = {styles.heroContent}>
    <p       className = {styles.eyebrow}>Donut alternative</p>
        <h1>Donut alternative: 1: 1 coffee plus 5-minute games</h1>
        <p className = {styles.heroText}>
          Donut is the random-coffee name HR already knows. If the search is 1: 1 coffee pairing, that
          name is already on the shortlist. Donut also sells onboarding and recognition. Trivia is
          pairing plus five-minute games.
        </p>
        <p className = {styles.heroText}>
          The gap shows up after the DM. Pairing gave two people a name. The channel still went quiet.
          Quizzes, Pictionary, Water Cooler, and Know Your Team are the five-minute habit that makes
          the intro stick. They should not need a second vendor or a Zoom night.
        </p>
        <p className = {styles.heroText}>
          VirtualCoffee pairs teammates 1                                                               : 1 in Slack and in Microsoft Teams, then drops ice breakers
          so            the first message has a prompt. Split Slack and Teams shops still get the same 1: 1. The
          same install runs those channel games where work already happens.
        </p>
        <p className = {styles.heroNote}>
          Pairing is live on both workspaces. Slack pairs can put the catch-up on the calendar. Teams
          pairing still sends the 1: 1 DM. People pick a time themselves. Hangman is a Slack game, not
          a Teams feature.
        </p>
        <CtaButtons className = {styles.heroCtas} />
      </div>
    </section>

    <div     className = {styles.content}>
    <section className = {styles.section} aria-labelledby = 'who-heading'>
    <h2      id        = 'who-heading'>Who this is for</h2>
        <p>
          People and HR leads who are shopping a Donut alternative because pairing alone did not keep
          the channel human. They want 1: 1 coffee and a five-minute game in Slack or Microsoft Teams,
          one install.
        </p>
        <p>
          Stay on Donut if intros, onboarding, and recognition are the whole program. Choose Trivia if
          the coffee chat should sit next to quizzes, not in a second app.
        </p>
      </section>

      <section className = {styles.section} aria-labelledby = 'how-heading'>
      <h2      id        = 'how-heading'>How it works</h2>
      <ol      className = {styles.steps}>
          <li>Add Trivia to Slack or to Microsoft Teams.</li>
          <li>
            Turn on VirtualCoffee in a channel. Trivia pairs people 1: 1 and DMs ice breakers.
          </li>
          <li>
            In the same channel, run Instant Quizzes, Pictionary, Water Cooler, or Know Your Team.
            Five minutes. No extra meeting.
          </li>
          <li>
            Repeat on a cadence you can keep. Every 2, 4, or 12 weeks is the usual range.
          </li>
        </ol>
      </section>

      <section className = {styles.section} aria-labelledby = 'compare-heading'>
      <h2      id        = 'compare-heading'>Slack and Teams</h2>
      <div     className = {styles.tableWrap}>
      <table   className = {styles.compareTable}>
            <thead>
              <tr>
                <th scope = 'col'>Need</th>
                <th scope = 'col'>Donut</th>
                <th scope = 'col'>Trivia</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row) => (
                <tr key   = {row.need}>
                <th scope = 'row'>{row.need}</th>
                  <td>{row.donut}</td>
                  <td>{row.trivia}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className = {styles.factList}>
          <li>VirtualCoffee pairing         : Slack and Teams.</li>
          <li>Ice           breakers and DMs: Slack and Teams.</li>
          <li>Instant       Quizzes,          Pictionary, Water Cooler, Know Your Team: Slack and Teams.</li>
          <li>
            Calendar hold: Slack only. Teams pairing still sends the 1: 1 DM. People pick a time
            themselves.
          </li>
          <li>Hangman: Slack only.</li>
        </ul>
      </section>

      <section className = {styles.quoteSection} aria-labelledby = 'quote-heading'>
      <h2      id        = 'quote-heading'>What one team says about the coffees</h2>
        <blockquote>
          <p>
            I love the Virtual Coffee feature, it&apos;s a great way to break the ice. I have seen so
            many instances where the Virtual Coffee pairings have fostered strong bonds. We&apos;ve
            seen people discussing topics outside of work.
          </p>
          <footer>
            <cite>Sonia Stefan</cite>, Recruiter at BetterQA
          </footer>
        </blockquote>
      </section>

      <section className = {styles.section} aria-labelledby = 'faq-heading'>
      <h2      id        = 'faq-heading'>FAQ</h2>
      <div     className = {styles.faqList}>
          {FAQ_ITEMS.map((item) => (
            <details className = {styles.faqItem} key = {item.question}>
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section    className = {styles.startSection} aria-labelledby = 'start-heading'>
      <h2         id        = 'start-heading'>Start</h2>
      <CtaButtons className = {styles.startCtas} />
      <p          className = {styles.startsFree}>Starts free.</p>
      <p          className = {styles.demoNote}>
          If you want a walkthrough: {' '}
          <a href = 'https://calendly.com/heronhr-info'>Book a short demo</a>
        </p>
      </section>
    </div>

    <BlogFooter />
  </main>
);

export default DonutAlternative;
